import React, { ComponentType, useMemo, useState } from "react";

import { useKpiTabToolbar } from "../KpiTabToolbarContext";
import { useClientCountry } from "../../../hooks/useClientCountry";

import type { Dayjs } from "dayjs";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import KpiCardJs from "../../shared/Dashboard/KpiCard/KpiCard";
import DatePickerJs from "../../shared/DatePicker/DatePicker";
import Button from "../../shared/Button/Button";
import ExportToXlsxButtonJs from "../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";

import CasinoDailyPerformance from "./CasinoDailyPerformance/CasinoDailyPerformance";
import CasinoTopFivePanel from "./CasinoTopFivePanel/CasinoTopFivePanel";
import CasinoByStateV2 from "./CasinoByStateV2/CasinoByStateV2";
import CasinoKpiFiltersSection, {
  EMPTY_CASINO_KPI_FILTERS,
} from "./CasinoKpiFiltersSection/CasinoKpiFiltersSection";

import { DATE_RANGE, KPI_CARD_BACKGROUND } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getAdminNow } from "../../../utils/dateService";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../utils/narrowViewportChartDates";
import {
  CARD_ORDER,
  useGetCasinoV2KpiCards,
} from "../../../queries/casino-kpis/useGetCasinoV2KpiCards";
import {
  useGetCasinoTopGames,
  useGetCasinoTopProviders,
} from "../../../queries/casino-kpis/useGetCasinoTopRankings";
import type { CasinoKpiFilterValues } from "../../../queries/casino-kpis/casinoKpiFilters";

import styles from "./CasinoKPIsV2.module.css";

const KpiCard = KpiCardJs as ComponentType<any>;
const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

const COUNT_KEYS = new Set(["total_rounds", "total_users"]);

interface CasinoKPIsV2Props {
  currencySymbol?: string;
}

const KPI_LABELS: Record<string, string> = {
  turnover: AppStrings.TURNOVER,
  ggr: AppStrings.GGR,
  margin_pct: AppStrings.MARGIN_PCT,
  total_rounds: AppStrings.TOTAL_ROUNDS,
  avg_bet_amount: AppStrings.AVG_BET_AMOUNT,
  total_users: AppStrings.TOTAL_ACTIVE_USERS,
};

// `DatePicker.getData` is required but the V2 page intentionally re-fetches
// reactively via `dateFilters` -> queryKey. Hoisting the no-op to module
// scope makes the intent obvious to readers and avoids a needless callback
// per render.
const NOOP_GET_DATA = (_queryString: string | null): void => undefined;

/**
 * Casino KPI Tab V2 — KPI cards, daily performance, and top-5 providers/games
 * are wired to /admin-panel-v2/casino/analytics/kpis*. The by-state map
 * remains on mock data pending its own integration PR.
 */
const CasinoKPIsV2: React.FC<CasinoKPIsV2Props> = ({ currencySymbol }) => {
  // TCM-driven country code → drives the CasinoByStateV2 geo map.
  const clientCountry = useClientCountry();

  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.startOf("month"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.CURRENT_MONTH),
  );
  const [appliedFilters, setAppliedFilters] = useState<CasinoKpiFilterValues>(
    EMPTY_CASINO_KPI_FILTERS,
  );
  const [filtersVisible, setFiltersVisible] = useState(false);

  // Date range drives the query keys, so changing it triggers a refetch.
  // Defensive `?.format() ?? ""` guards against a future change to the date
  // picker (e.g. enabling `allowClearToNull`) handing us a missing endpoint
  // — the hook treats empty strings as "disabled" and skips the request.
  const queryFilters = useMemo(
    () => ({
      start_date: dates[0]?.format("YYYY-MM-DD") ?? "",
      end_date: dates[1]?.format("YYYY-MM-DD") ?? "",
      ...appliedFilters,
    }),
    [appliedFilters, dates],
  );

  const {
    data: kpiResult,
    isFetching: isCardsLoading,
    isError,
  } = useGetCasinoV2KpiCards(queryFilters);
  const kpiCards = useMemo(() => kpiResult?.cards ?? [], [kpiResult?.cards]);
  const filterOptions = kpiResult?.options ?? [];

  // Skeleton when there is no data yet AND we are loading. Empty state when
  // the request resolved to an empty payload without an error (the error
  // banner above handles `isError`).
  const showSkeleton = isCardsLoading && kpiCards.length === 0;
  const showEmpty = !showSkeleton && !isError && kpiCards.length === 0;

  const exportRows = useMemo(
    () =>
      kpiCards.map((item) => ({
        metric: KPI_LABELS[item.key] ?? item.key,
        value: item.value,
        percent_change: item.percentage,
      })),
    [kpiCards],
  );

  const tabToolbar = useMemo(
    () => (
      <>
        <Button
          onClick={() => setFiltersVisible((visible) => !visible)}
          aria-expanded={filtersVisible}
          aria-label={AppStrings.PAM_REPORT_FILTERS}
        >
          <Filter size={18} aria-hidden="true" />
          {filtersVisible ? (
            <ChevronUp size={16} aria-hidden="true" />
          ) : (
            <ChevronDown size={16} aria-hidden="true" />
          )}
        </Button>
        <CustomDatePicker
          getData={NOOP_GET_DATA}
          dates={dates}
          setDates={setDates}
          showPresets={true}
          startRange={startRange}
        />
        <ExportToXlsxButton data={exportRows} filename="casino-kpis.xlsx" />
      </>
    ),
    [dates, exportRows, filtersVisible, startRange],
  );

  useKpiTabToolbar(tabToolbar);

  return (
    <div className={styles.casinoV2}>
      {isError && (
        <div className={styles.errorBanner} data-testid="casino-kpi-error">
          {AppStrings.CASINO_KPI_CARDS_ERROR}
        </div>
      )}

      {filtersVisible && (
        <CasinoKpiFiltersSection
          value={appliedFilters}
          options={filterOptions}
          loading={isCardsLoading}
          onApply={setAppliedFilters}
        />
      )}

      <div className={styles.kpiGrid} data-testid="casino-kpi-grid">
        {showSkeleton ? (
          CARD_ORDER.map((key) => (
            <KpiCard
              key={key}
              isApiRunning={true}
              title={KPI_LABELS[key] ?? key}
              value={0}
              percentage={0}
              rate={false}
              currencySymbol={currencySymbol}
              hugeValue={false}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
            />
          ))
        ) : showEmpty ? (
          <div className={styles.emptyState} data-testid="casino-kpi-empty">
            {AppStrings.NO_DATA_AVAILABLE}
          </div>
        ) : (
          kpiCards.map((item) => (
            <KpiCard
              key={item.key}
              isApiRunning={isCardsLoading}
              title={KPI_LABELS[item.key] ?? item.key}
              value={item.value}
              percentage={item.percentage}
              rate={item.rate}
              maximumFractionDigits={
                item.rate ? 2 : COUNT_KEYS.has(item.key) ? 0 : 2
              }
              currencySymbol={currencySymbol}
              hugeValue={false}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
            />
          ))
        )}
      </div>

      <CasinoDailyPerformance
        moneyTypeOptions={filterOptions}
      />

      <div className={styles.topFiveRow}>
        <CasinoTopFivePanel
          title={AppStrings.CASINO_TOP_PROVIDERS}
          useData={useGetCasinoTopProviders}
          exportFilename="casino-top-providers.xlsx"
          moneyTypeOptions={filterOptions}
        />
        <CasinoTopFivePanel
          title={AppStrings.CASINO_TOP_GAMES}
          useData={useGetCasinoTopGames}
          exportFilename="casino-top-games.xlsx"
          moneyTypeOptions={filterOptions}
        />
      </div>

      <CasinoByStateV2
        country={clientCountry}
        moneyTypeOptions={filterOptions}
      />
    </div>
  );
};

export default CasinoKPIsV2;
